import { FC } from 'react';

import { Head, useForm, usePage } from '@inertiajs/react';
import { Mail, MapPin, Loader2, CheckCircle2, Send } from 'lucide-react';

import { TELEGRAM_URL } from '../../components/shared/siteNavigation';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Textarea } from '../../components/ui/Textarea';

type PageProps = {
    flash: {
        notice: string;
        alert?: string;
    };
};

type FormValues = {
    name: string;
    email: string;
    subject: string;
    message: string;
};

const Contact: FC = () => {
    const { flash } = usePage<PageProps>().props;
    const { data, setData, post, processing, errors, reset, transform } =
        useForm<FormValues>({
            name: '',
            email: '',
            subject: '',
            message: '',
        });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        transform((data) => ({ contact: data }));
        post('/contact', {
            onSuccess: () => reset(),
        });
    };

    return (
        <>
            <Head title="Зв'яжіться з нами | CartOn" />
            <div className="container mx-auto max-w-4xl py-10">
                <div className="mb-10">
                    <h1 className="mb-4 text-3xl font-bold">
                        Зв&#39;яжіться з нами
                    </h1>
                    <p className="text-lg text-muted-foreground">
                        Маєте запитання, пропозиції або потребуєте допомоги? Ми
                        завжди раді почути вас! Заповніть форму, напишіть на
                        електронну пошту або зверніться в Telegram — там ми
                        відповідаємо найшвидше.
                    </p>
                </div>

                <div className="grid gap-10 md:grid-cols-2">
                    <div>
                        <h2 className="mb-6 text-xl font-semibold">
                            Наші контакти
                        </h2>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Send className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium">Telegram</p>
                                    <a
                                        href={TELEGRAM_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-primary transition-colors hover:underline"
                                    >
                                        Написати в Telegram
                                    </a>
                                    <p className="text-xs text-muted-foreground">
                                        У Telegram відповідаємо найшвидше.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <Mail className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium">Email</p>
                                    <a
                                        href="mailto:carton.market.ua@gmail.com"
                                        className="text-sm text-muted-foreground transition-colors hover:text-primary"
                                    >
                                        carton.market.ua@gmail.com
                                    </a>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium">Адреса</p>
                                    <p className="text-sm text-muted-foreground">
                                        Івано-Франківськ, Україна
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border bg-card p-6 shadow-sm">
                        {flash.notice && (
                            <Alert className="mb-6 border-green-200 bg-green-50 text-green-800">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <AlertTitle>Успішно!</AlertTitle>
                                <AlertDescription className="text-green-800">
                                    {flash.notice}
                                </AlertDescription>
                            </Alert>
                        )}

                        {flash.alert && (
                            <Alert variant="destructive" className="mb-6">
                                <AlertTitle>Помилка</AlertTitle>
                                <AlertDescription>
                                    {flash.alert}
                                </AlertDescription>
                            </Alert>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Ім&#39;я</Label>
                                    <Input
                                        id="name"
                                        placeholder="Ваше ім'я"
                                        required
                                        value={data.name}
                                        onChange={(e) =>
                                            setData('name', e.target.value)
                                        }
                                        disabled={processing}
                                    />
                                    {errors.name && (
                                        <p className="text-sm text-destructive">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="email@example.com"
                                        required
                                        value={data.email}
                                        onChange={(e) =>
                                            setData('email', e.target.value)
                                        }
                                        disabled={processing}
                                    />
                                    {errors.email && (
                                        <p className="text-sm text-destructive">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="subject">Тема</Label>
                                <Input
                                    id="subject"
                                    placeholder="Тема повідомлення"
                                    required
                                    value={data.subject}
                                    onChange={(e) =>
                                        setData('subject', e.target.value)
                                    }
                                    disabled={processing}
                                />
                                {errors.subject && (
                                    <p className="text-sm text-destructive">
                                        {errors.subject}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="message">Повідомлення</Label>
                                <Textarea
                                    id="message"
                                    placeholder="Ваше повідомлення..."
                                    className="min-h-[120px]"
                                    required
                                    value={data.message}
                                    onChange={(e) =>
                                        setData('message', e.target.value)
                                    }
                                    disabled={processing}
                                />
                                {errors.message && (
                                    <p className="text-sm text-destructive">
                                        {errors.message}
                                    </p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={processing}
                            >
                                {processing ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Надсилання...
                                    </>
                                ) : (
                                    'Надіслати повідомлення'
                                )}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;
